import React, { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserRole } from "./types";
import { useAuth } from "./hooks/useAuth";
import { useInactivityLogout } from "./hooks/useInactivityLogout";
import { LoadingFallback } from "./components/layout/LoadingFallback";
import { AppLayout } from "./components/layout/AppLayout";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { SiteProvider } from "./contexts/SiteContext";

// Lazy load all page components for code splitting
const AuthPage = lazy(() => import("./pages/Auth").then(module => ({ default: module.AuthPage })));
const Index = lazy(() => import("./pages/Index"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/AdminDashboard").then(module => ({ default: module.AdminDashboard })));
const AdminSpecialists = lazy(() => import("./pages/AdminSpecialists"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminActivityLogs = lazy(() => import("./pages/AdminActivityLogs"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminQualifications = lazy(() => import("./pages/AdminQualifications"));
const AdminPaymentReconciliation = lazy(() => import("./pages/AdminPaymentReconciliation"));
const AdminPPM = lazy(() => import("./pages/AdminPPM"));
const AdminGDPRDashboard = lazy(() => import("./components/AdminGDPRDashboard").then(module => ({ default: module.AdminGDPRDashboard })));

// Specialist pages
const SpecialistDashboard = lazy(() => import("./pages/SpecialistDashboard").then(module => ({ default: module.SpecialistDashboard })));
const SpecialistProfile = lazy(() => import("./pages/SpecialistProfile").then(module => ({ default: module.SpecialistProfile })));
const SpecialistJobs = lazy(() => import("./pages/SpecialistJobs").then(module => ({ default: module.SpecialistJobs })));
const SpecialistEarnings = lazy(() => import("./pages/SpecialistEarnings").then(module => ({ default: module.SpecialistEarnings })));
const SpecialistInventory = lazy(() => import("./pages/SpecialistInventory").then(module => ({ default: module.SpecialistInventory })));
const SpecialistSettings = lazy(() => import("./pages/SpecialistSettings").then(module => ({ default: module.SpecialistSettings })));
const SpecialistPPMJobs = lazy(() => import("./pages/SpecialistPPMJobs"));
const SpecialistPPMDetail = lazy(() => import("./pages/SpecialistPPMDetail"));
const SpecialistReports = lazy(() => import("./pages/SpecialistReports"));

// Partner pages
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard").then(module => ({ default: module.PartnerDashboard })));
const PartnerAssets = lazy(() => import("./pages/PartnerAssets").then(module => ({ default: module.PartnerAssets })));
const AssetDetail = lazy(() => import("./pages/AssetDetail").then(module => ({ default: module.AssetDetail })));
const PartnerJobs = lazy(() => import("./pages/PartnerJobs").then(module => ({ default: module.PartnerJobs })));
const PartnerInvoices = lazy(() => import("./pages/PartnerInvoices").then(module => ({ default: module.PartnerInvoices })));
const PartnerSettings = lazy(() => import("./pages/PartnerSettings").then(module => ({ default: module.PartnerSettings })));
const PartnerPPM = lazy(() => import("./pages/PartnerPPM"));
const PartnerPPMDetail = lazy(() => import("./pages/PartnerPPMDetail"));
const PartnerPPMReport = lazy(() => import("./pages/PartnerPPMReport"));
const PartnerReports = lazy(() => import("./pages/PartnerReports"));

// Shared pages
const JobDetails = lazy(() => import("./pages/JobDetails").then(module => ({ default: module.JobDetails })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").then(module => ({ default: module.PrivacyPolicy })));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions").then(module => ({ default: module.TermsAndConditions })));

// Onboarding pages
const SpecialistOnboarding = lazy(() => import("./pages/onboarding/specialist/SpecialistOnboarding"));
const PartnerOnboarding = lazy(() => import("./pages/onboarding/partner/PartnerOnboarding"));
const ManagerOnboarding = lazy(() => import("./pages/onboarding/manager/ManagerOnboarding"));

// Manager pages
const ManagerDashboard = lazy(() => import("./pages/ManagerDashboard").then(module => ({ default: module.ManagerDashboard })));
const ManagerSites = lazy(() => import("./pages/ManagerSites").then(module => ({ default: module.ManagerSites })));
const ManagerSiteDetail = lazy(() => import("./pages/ManagerSiteDetail").then(module => ({ default: module.ManagerSiteDetail })));
const ManagerTeam = lazy(() => import("./pages/ManagerTeam").then(module => ({ default: module.ManagerTeam })));
const ManagerAssets = lazy(() => import("./pages/ManagerAssets").then(module => ({ default: module.ManagerAssets })));
const ManagerSettings = lazy(() => import("./pages/ManagerSettings").then(module => ({ default: module.ManagerSettings })));
const ManagerReports = lazy(() => import("./pages/ManagerReports"));

// Invitation pages
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation").then(module => ({ default: module.AcceptInvitation })));

const queryClient = new QueryClient();

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredRole?: UserRole;
  auth: {
    isAuthenticated: boolean;
    userRole: UserRole | null;
    loading: boolean;
  };
}> = ({ children, requiredRole, auth }) => {
  const { isAuthenticated, userRole, loading } = auth;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-2">Checking permissions...</div>
          <div className="text-sm text-muted-foreground">Please wait</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (!userRole) {
    // Role is loaded asynchronously after session; don't bounce to /auth (causes redirect loops)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-2">Checking permissions...</div>
          <div className="text-sm text-muted-foreground">Loading your role</div>
        </div>
      </div>
    );
  }

  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to={`/${userRole}`} replace />;
  }

  return <>{children}</>;
};

const App = () => {
  const { isAuthenticated, loading, userRole } = useAuth();
  const auth = { isAuthenticated, loading, userRole };
  
  useInactivityLogout(isAuthenticated);

  // Single return to avoid hook order issues
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <SonnerToaster />
        {loading ? (
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <div className="text-lg">Loading your account...</div>
              <div className="text-sm text-muted-foreground">
                Auth: {isAuthenticated ? '✓' : '○'} | Role: {userRole || '○'}
              </div>
            </div>
          </div>
        ) : (
          <>
            <CookieConsentBanner />
            <BrowserRouter>
              <Routes>
                {/* Public Routes */}
                <Route path="/privacy-policy" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PrivacyPolicy />
                  </Suspense>
                } />
                <Route path="/terms" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <TermsAndConditions />
                  </Suspense>
                } />

                {/* Accept Invitation Route - public but redirects to auth if needed */}
                <Route path="/accept-invitation" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AcceptInvitation />
                  </Suspense>
                } />
                
                {/* Auth Route */}
                <Route path="/auth" element={
                  <Suspense fallback={<LoadingFallback />}>
                    {(() => {
                      const hashParams = new URLSearchParams(window.location.hash.substring(1));
                      const queryParams = new URLSearchParams(window.location.search);
                      const isRecoveryHash = hashParams.get('type') === 'recovery';
                      const isRecoveryQuery = queryParams.get('type') === 'recovery';
                      const resetFlag = queryParams.get('reset') === 'true';
                      const isRecoveryFlow = sessionStorage.getItem('password_recovery_flow') === 'true';
                      
                      if (isRecoveryHash || isRecoveryQuery || resetFlag || isRecoveryFlow) {
                        return <AuthPage />;
                      }
                      
                      return isAuthenticated && userRole ? 
                        <Navigate to={`/${userRole}`} replace /> : 
                        <AuthPage />;
                    })()}
                  </Suspense>
                } />
                
                {/* Root redirect */}
                <Route path="/" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Index />
                  </Suspense>
                } />
                
                {/* Admin Routes */}
                <Route path="/admin" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminDashboard />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/specialists" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminSpecialists />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminUsers />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/activity-logs" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminActivityLogs />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/settings" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminSettings />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/qualifications" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminQualifications />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/reconciliation" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminPaymentReconciliation />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/ppm" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminPPM />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/gdpr" element={
                  <ProtectedRoute auth={auth} requiredRole="admin">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminGDPRDashboard />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                
                {/* Specialist Routes */}
                <Route path="/specialist" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <SpecialistDashboard />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/profile" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <SpecialistProfile />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/jobs" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <SpecialistJobs />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/earnings" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <SpecialistEarnings />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/inventory" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <SpecialistInventory />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/jobs/:jobId" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <JobDetails />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/settings" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <SpecialistSettings />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/ppm" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <Suspense fallback={<LoadingFallback />}>
                      <SpecialistPPMJobs />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/ppm/:id" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <Suspense fallback={<LoadingFallback />}>
                      <SpecialistPPMDetail />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/specialist/reports" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <SpecialistReports />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                
                {/* Partner Routes */}
                <Route path="/partner" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <PartnerDashboard />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/partner/assets" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <PartnerAssets />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/partner/assets/:assetId" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AssetDetail />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/partner/jobs" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <PartnerJobs />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/partner/jobs/:jobId" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <JobDetails />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/partner/invoices" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <PartnerInvoices />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/partner/reports" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <PartnerReports />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/partner/settings" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <AppLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <PartnerSettings />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/partner/ppm" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <Suspense fallback={<LoadingFallback />}>
                      <PartnerPPM />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/partner/ppm/:id" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <Suspense fallback={<LoadingFallback />}>
                      <PartnerPPMDetail />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/partner/ppm/report/:id" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <Suspense fallback={<LoadingFallback />}>
                      <PartnerPPMReport />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Manager Routes */}
                <Route path="/manager" element={
                  <ProtectedRoute auth={auth} requiredRole="manager">
                    <SiteProvider>
                      <AppLayout>
                        <Suspense fallback={<LoadingFallback />}>
                          <ManagerDashboard />
                        </Suspense>
                      </AppLayout>
                    </SiteProvider>
                  </ProtectedRoute>
                } />
                <Route path="/manager/sites" element={
                  <ProtectedRoute auth={auth} requiredRole="manager">
                    <SiteProvider>
                      <AppLayout>
                        <Suspense fallback={<LoadingFallback />}>
                          <ManagerSites />
                        </Suspense>
                      </AppLayout>
                    </SiteProvider>
                  </ProtectedRoute>
                } />
                <Route path="/manager/sites/:siteId" element={
                  <ProtectedRoute auth={auth} requiredRole="manager">
                    <SiteProvider>
                      <AppLayout>
                        <Suspense fallback={<LoadingFallback />}>
                          <ManagerSiteDetail />
                        </Suspense>
                      </AppLayout>
                    </SiteProvider>
                  </ProtectedRoute>
                } />
                <Route path="/manager/team" element={
                  <ProtectedRoute auth={auth} requiredRole="manager">
                    <SiteProvider>
                      <AppLayout>
                        <Suspense fallback={<LoadingFallback />}>
                          <ManagerTeam />
                        </Suspense>
                      </AppLayout>
                    </SiteProvider>
                  </ProtectedRoute>
                } />
                <Route path="/manager/assets" element={
                  <ProtectedRoute auth={auth} requiredRole="manager">
                    <SiteProvider>
                      <AppLayout>
                        <Suspense fallback={<LoadingFallback />}>
                          <ManagerAssets />
                        </Suspense>
                      </AppLayout>
                    </SiteProvider>
                  </ProtectedRoute>
                } />
                <Route path="/manager/settings" element={
                  <ProtectedRoute auth={auth} requiredRole="manager">
                    <SiteProvider>
                      <AppLayout>
                        <Suspense fallback={<LoadingFallback />}>
                          <ManagerSettings />
                        </Suspense>
                      </AppLayout>
                    </SiteProvider>
                  </ProtectedRoute>
                } />
                <Route path="/manager/reports" element={
                  <ProtectedRoute auth={auth} requiredRole="manager">
                    <SiteProvider>
                      <AppLayout>
                        <Suspense fallback={<LoadingFallback />}>
                          <ManagerReports />
                        </Suspense>
                      </AppLayout>
                    </SiteProvider>
                  </ProtectedRoute>
                } />

                <Route path="/privacy" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PrivacyPolicy />
                  </Suspense>
                } />
                
                {/* Onboarding Routes */}
                <Route path="/onboarding/specialist" element={
                  <ProtectedRoute auth={auth} requiredRole="specialist">
                    <Suspense fallback={<LoadingFallback />}>
                      <SpecialistOnboarding />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/partner" element={
                  <ProtectedRoute auth={auth} requiredRole="partner">
                    <Suspense fallback={<LoadingFallback />}>
                      <PartnerOnboarding />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/manager" element={
                  <ProtectedRoute auth={auth} requiredRole="manager">
                    <Suspense fallback={<LoadingFallback />}>
                      <ManagerOnboarding />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                <Route path="*" element={<Navigate to="/auth" replace />} />
              </Routes>
            </BrowserRouter>
          </>
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
